import React, { FC } from 'react'
import { useTheme } from 'styled-components'

import FormikField from '../../form/FormikField'
import { FormikFormProps } from '../../form/FormikForm'
import { ActionButtonVariant } from '../../components/ActionButton/styled'
import { FormSubmitButtonProps } from '../../form/FormSubmitButton'
import FormMessages, { FormMessagesProps } from '../../form/FormMessages'
import { FormFieldInputProps } from '../../form/fields/FormFieldInput'
import { FormFieldSelectProps } from '../../form/fields/FormFieldSelect'
import { FormFieldFileProps } from '../../form/fields/FormFieldFile'
import { FormFieldCheckboxProps } from '../../form/fields/FormFieldCheckbox'
import { FormFieldDateProps } from '../../form/fields/FormFieldDate'
import { FormFieldPhoneProps } from '../../form/fields/FormFieldPhone'
import { Classified } from '../../rest/types/Classified'

import * as SC from './styled'

export type AdsReplyFormValues = {
  type: any
  civility: string
  surname: string
  name: string
  email: string
  phone: string
  dateOfBirth?: string
  address?: {
    street: string
    additional: string
    city: string
    postalCode: string
    country: string
  }
  studyLevel?: string
  university?: string
  sessionDuration?: string
  sessionDurationCarabin?: string
  availability?: {
    firstDate: string
    secondDate: string
    thirdDate: string
    fourthDate: string
    authorisationContact: boolean
  }
  uploadStudentCard?: File | null
  message: string
  file: File | null
  chartOpt?: boolean
  rulesOpt?: boolean
  fullClassified?: Classified
  answer_user?: string
  classified?: string
}

export type AdsReplyFormProps = FormikFormProps<AdsReplyFormValues> & {
  fields: {
    civility: Omit<FormFieldSelectProps, 'name'>
    surname: Omit<FormFieldInputProps, 'name'>
    name: Omit<FormFieldInputProps, 'name'>
    email: Omit<FormFieldInputProps, 'name'>
    phone: Omit<FormFieldPhoneProps, 'name'>
    dateOfBirth?: Omit<FormFieldDateProps, 'name'>
    address?: {
      title: string
      street: Omit<FormFieldInputProps, 'name'>
      additional: Omit<FormFieldInputProps, 'name'>
      city: Omit<FormFieldInputProps, 'name'>
      postalCode: Omit<FormFieldInputProps, 'name'>
      country: Omit<FormFieldInputProps, 'name'>
    }
    studyLevel?: Omit<FormFieldSelectProps, 'name'>
    university?: Omit<FormFieldSelectProps, 'name'>
    sessionDuration?: Omit<FormFieldSelectProps, 'name'>
    sessionDurationCarabin?: Omit<FormFieldInputProps, 'name'>
    availability?: {
      title: string
      firstDate: Omit<FormFieldDateProps, 'name'>
      secondDate: Omit<FormFieldDateProps, 'name'>
      thirdDate: Omit<FormFieldDateProps, 'name'>
      fourthDate: Omit<FormFieldDateProps, 'name'>
      authorisationContact: Omit<FormFieldCheckboxProps, 'name'>
    }
    uploadStudentCard?: Omit<FormFieldFileProps, 'name'>
    message: Omit<FormFieldInputProps, 'name'>
    file: Omit<FormFieldFileProps, 'name'>
    chartOpt?: Omit<FormFieldCheckboxProps, 'name'> & {
      link: React.ReactNode
    }
    rulesOpt?: Omit<FormFieldCheckboxProps, 'name'> & {
      title: string
      link: React.ReactNode
    }
  }
  submitButton?: FormSubmitButtonProps
  errors?: FormMessagesProps['messages']
  submissionValidationText?: string
}

const AdsReplyForm: FC<AdsReplyFormProps> = ({
  fields,
  submitButton,
  errors,
  submissionValidationText,
  ...formikProps
}) => {
  const {
    civility,
    surname,
    name,
    email,
    phone,
    dateOfBirth,
    address,
    studyLevel,
    university,
    sessionDuration,
    sessionDurationCarabin,
    uploadStudentCard,
    availability,
    message,
    file,
    chartOpt,
    rulesOpt,
  } = fields
  const theme = useTheme()

  return (
    <SC.Container>
      {!submissionValidationText ? (
        <SC.Form {...formikProps}>
          {() => {
            return (
              <>
                <SC.Fields>
                  <FormikField
                    {...civility}
                    labelProps={{ underlined: true }}
                    name="civility"
                    Component={SC.Select}
                  />
                  <FormikField
                    {...surname}
                    labelProps={{ underlined: true }}
                    name="surname"
                    type="text"
                    Component={SC.Input}
                    innerError
                  />
                  <FormikField
                    {...name}
                    name="name"
                    type="text"
                    labelProps={{ underlined: true }}
                    Component={SC.Input}
                    innerError
                  />
                  <FormikField
                    {...email}
                    name="email"
                    type="email"
                    labelProps={{ underlined: true }}
                    Component={SC.Input}
                    leftAdornment={
                      <SC.UploadIcon
                        icon="postmail"
                        width={20}
                        height={20}
                        color={theme.colors.greyContour}
                      />
                    }
                    innerError
                  />
                  <FormikField
                    {...phone}
                    name="phone"
                    type="tel"
                    labelProps={{ underlined: true }}
                    Component={SC.PhoneField}
                    leftAdornment={
                      <SC.UploadIcon
                        icon="phone"
                        width={20}
                        height={20}
                        color={theme.colors.greyContour}
                      />
                    }
                    innerError
                  />
                  {dateOfBirth && (
                    <FormikField
                      {...dateOfBirth}
                      name="dateOfBirth"
                      labelProps={{ underlined: true }}
                      Component={SC.DateField}
                      innerError
                    />
                  )}
                  {address && (
                    <SC.AddressBlock>
                      <SC.AddressTitle>{address.title}</SC.AddressTitle>
                      <SC.Line />
                      <SC.AddressFieldsWrapper>
                        <FormikField
                          {...address.street}
                          name="address.street"
                          type="text"
                          labelProps={{ underlined: true }}
                          Component={SC.Input}
                          innerError
                        />
                        <FormikField
                          {...address.additional}
                          name="address.additional"
                          type="text"
                          labelProps={{ underlined: true }}
                          Component={SC.Input}
                          innerError
                        />
                        <SC.LastAddressFieldsWrapper>
                          <FormikField
                            {...address.city}
                            name="address.city"
                            type="text"
                            labelProps={{ underlined: true }}
                            Component={SC.Input}
                            innerError
                          />
                          <FormikField
                            {...address.postalCode}
                            name="address.postalCode"
                            type="text"
                            labelProps={{ underlined: true }}
                            Component={SC.Input}
                            innerError
                          />
                          <FormikField
                            {...address.country}
                            name="address.country"
                            type="text"
                            labelProps={{ underlined: true }}
                            Component={SC.Input}
                            innerError
                          />
                        </SC.LastAddressFieldsWrapper>
                      </SC.AddressFieldsWrapper>
                    </SC.AddressBlock>
                  )}
                  {studyLevel && (
                    <FormikField
                      {...studyLevel}
                      labelProps={{ underlined: true }}
                      name="studyLevel"
                      Component={SC.Select}
                    />
                  )}
                  {university && (
                    <FormikField
                      {...university}
                      labelProps={{ underlined: true }}
                      name="university"
                      Component={SC.Select}
                    />
                  )}
                  {uploadStudentCard && (
                    <FormikField
                      {...uploadStudentCard}
                      name="uploadStudentCard"
                      labelProps={{ underlined: true }}
                      Component={SC.UploadFile}
                      innerError
                    />
                  )}
                  {sessionDuration && (
                    <FormikField
                      {...sessionDuration}
                      name="sessionDuration"
                      labelProps={{ underlined: true }}
                      Component={SC.Select}
                      innerError
                    />
                  )}
                  {sessionDurationCarabin && (
                    <FormikField
                      {...sessionDurationCarabin}
                      name="sessionDurationCarabin"
                      labelProps={{ underlined: true }}
                      Component={SC.Input}
                      innerError
                    />
                  )}
                  {availability && (
                    <SC.AvailabilityBlock>
                      <SC.AddressTitle>{availability.title}</SC.AddressTitle>
                      <SC.Line />
                      <SC.AvailabilityAddressWrapperFirstLine>
                        <FormikField
                          {...availability.firstDate}
                          name="availability.firstDate"
                          Component={SC.DateField}
                          innerError
                        />
                        <FormikField
                          {...availability.secondDate}
                          name="availability.secondDate"
                          Component={SC.DateField}
                          innerError
                        />
                      </SC.AvailabilityAddressWrapperFirstLine>
                      <SC.AvailabilityAddressWrapperSecondLine>
                        <FormikField
                          {...availability.thirdDate}
                          name="availability.thirdDate"
                          Component={SC.DateField}
                          innerError
                        />
                        <FormikField
                          {...availability.fourthDate}
                          name="availability.fourthDate"
                          Component={SC.DateField}
                          innerError
                        />
                      </SC.AvailabilityAddressWrapperSecondLine>

                      <FormikField
                        {...availability.authorisationContact}
                        name="availability.authorisationContact"
                        Component={SC.CheckboxField}
                        innerError
                      />
                    </SC.AvailabilityBlock>
                  )}
                  <FormikField
                    {...message}
                    name="message"
                    labelProps={{ underlined: true }}
                    Component={SC.TextArea}
                    innerError
                  />
                  <FormikField
                    {...file}
                    name="file"
                    labelProps={{ underlined: true }}
                    Component={SC.UploadFile}
                    leftAdornment={
                      <SC.UploadIcon
                        icon="doc"
                        width={20}
                        height={20}
                        color={theme.colors.greyContour}
                      />
                    }
                    buttonIconType={'doc'}
                    innerError
                  />
                </SC.Fields>
                {rulesOpt && (
                  <SC.Chart>
                    <SC.AddressTitle>{rulesOpt.title}</SC.AddressTitle>
                    <SC.Line />
                    <SC.ChartTitleLink>{rulesOpt.link}</SC.ChartTitleLink>
                    <FormikField
                      {...rulesOpt}
                      colorVariant="Secondary"
                      Component={SC.CheckboxField}
                      name="rulesOpt"
                    />
                  </SC.Chart>
                )}
                {chartOpt && (
                  <SC.Chart>
                    <SC.ChartTitleLink>{chartOpt.link}</SC.ChartTitleLink>
                    <FormikField
                      {...chartOpt}
                      colorVariant="Secondary"
                      Component={SC.CheckboxField}
                      name="chartOpt"
                    />
                  </SC.Chart>
                )}
                {errors && <FormMessages messages={errors} />}
                <SC.Actions>
                  <SC.SubmitButton
                    type={'submit'}
                    variant={ActionButtonVariant.quaternary}
                    {...submitButton}
                  />
                </SC.Actions>
              </>
            )
          }}
        </SC.Form>
      ) : (
        <SC.SubmissionValidationText>
          {submissionValidationText}
        </SC.SubmissionValidationText>
      )}
    </SC.Container>
  )
}

export default AdsReplyForm
